
import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface VerificationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  codeSent: boolean;
}

export function VerificationCodeInput({ value, onChange, codeSent }: VerificationCodeInputProps) {
  const [individualDigits, setIndividualDigits] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Focus first input when code is sent
  useEffect(() => {
    if (codeSent && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [codeSent]);

  // Update individual digits when value changes
  useEffect(() => {
    const digits = value.split('').slice(0, 6);
    const updatedDigits = Array(6).fill('').map((_, i) => digits[i] || '');
    setIndividualDigits(updatedDigits);
  }, [value]);

  const handleDigitChange = (index: number, digit: string) => {
    if (digit.length > 1) {
      digit = digit.charAt(digit.length - 1);
    }

    if (!/^\d*$/.test(digit)) {
      return;
    }

    const newDigits = [...individualDigits];
    newDigits[index] = digit;
    setIndividualDigits(newDigits);

    const newValue = newDigits.join('');
    onChange(newValue);

    // Move focus to next input if this one is filled
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !individualDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.substring(0, 6).split('');
    const newDigits = [...individualDigits];
    
    digits.forEach((digit, index) => {
      if (index < 6) {
        newDigits[index] = digit;
      }
    });

    setIndividualDigits(newDigits);
    onChange(newDigits.join(''));
    
    // Focus last filled input or next empty one
    const lastIndex = Math.min(digits.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  return (
    <div className="flex flex-col items-center space-y-2">
      <p className="text-sm text-muted-foreground">
        Enter the 6-digit code sent to your phone
      </p>
      <div className="flex gap-2 justify-center">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="relative">
            <Input
              ref={el => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              className={cn(
                "w-10 h-12 text-center text-lg font-medium focus:ring-2 focus:ring-offset-2",
                individualDigits[index] ? "border-primary" : ""
              )}
              value={individualDigits[index]}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              maxLength={1}
              aria-label={`Digit ${index + 1}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
